/* Bag artwork for one-bag (tote) personalizers — the drawing that answers
   "what do I actually get?" before any photo is uploaded.

   It fills the step-1 preview panel, which appears once the customer has
   chosen a count. The order type picks the scene:

     full   the tote with N charms clipped where the handle joins it — two
            hang from the same handle end, cords crossing; the third moves
            over to the other end
     tote   the tote alone, N pet portraits across its front
     charm  N charms on their own, no bag

   The bag is the SAME cartoon tote the colour step previews, so step 1 and
   step 2 show one product rather than two drawings of it; toteSvg() lives
   here and personalizer-render-steps.js borrows it for the mockups. Charms
   reuse the symbols defined in personalizer-embed.liquid; the head-only
   portraits are drawn here (a symbol carries a body and a hanging cord, and
   an embroidered bag front must show neither).

   window.PersonalizerBagArt
     .svg(orderType, n, kind, hex)  -> the scene, as SVG markup
     .toteSvg(hex)                  -> the plain tote on its own */
(function () {
  'use strict';

  var CORD = '#8b7355';
  var RING = '#a89878';
  /* Before a colour is chosen the bag wears the brand's forest, which is also
     what the type-card icons wear — never a palette colour, which would read
     as a choice already made. */
  var DEFAULT_HEX = '#417746';

  var VIEWBOX = '0 0 120 112';
  /* The tote keeps its own 120x104 coordinates, nudged down so the handle
     clears the top: body then spans x 16..104, y 35..108, straps x 30..39 and
     81..90, and the handle meets the bag at (38,31) and (82,31). */
  var BAG_SHIFT = 'translate(0,5)';
  var CLIP_Y = 33;

  /* [x, top, tilt, ringX] per charm, drawn back to front. They clip where the
     handle joins the bag — the one place on a tote a charm actually hangs —
     and fall down the strap in front of it. The pair shares one handle end and
     swaps rings, so their cords cross the way two charms on one clip do. */
  var CHARMS_ON_BAG = {
    1: [[85, 39, 3, 82]],
    2: [[77, 39, -7, 86], [90, 45, 8, 78]],
    3: [[77, 39, -7, 86], [90, 45, 8, 78], [35, 41, -6, 39]]
  };
  /* Portraits sit between the two straps (x 39..81) and shrink as they
     multiply so the row always clears them. */
  var FACES = {
    1: { s: 0.9, x: [60] },
    2: { s: 0.63, x: [50, 70] },
    3: { s: 0.45, x: [46, 60, 74] }
  };
  /* No bag to hang from: the charms get the whole frame and their own rings. */
  var SOLO = {
    1: [[60, 38, 0]],
    2: [[46, 36, -6], [74, 42, 7]],
    3: [[32, 38, -5], [60, 34, 0], [88, 40, 6]]
  };

  function safeHex(hex) {
    // hex lands unescaped in fill/stroke attributes: accept only a real hex
    // colour (config and metafield data are not trusted), else the default.
    return /^#[0-9a-fA-F]{3,8}$/.test(String(hex)) ? String(hex) : DEFAULT_HEX;
  }

  /* Cartoon tote: white body, coloured handles / side straps / bottom. White
     totes get a light outline so they stay visible on the card. */
  function toteBody(hex) {
    hex = safeHex(hex);
    var isWhite = hex.toLowerCase() === '#ffffff';
    var stroke = isWhite ? '#d8d8d8' : hex;
    return '<path d="M38 26 C38 6 82 6 82 26" fill="none" stroke="' + stroke + '" stroke-width="7" stroke-linecap="round"/>'
      + '<path d="M44 26 C44 12 76 12 76 26" fill="none" stroke="#e7e7e7" stroke-width="3" stroke-linecap="round"/>'
      + '<path d="M16 30 h88 l-7 66 a8 8 0 0 1 -8 7 h-58 a8 8 0 0 1 -8 -7 z" fill="#ffffff" stroke="#e0ddd6" stroke-width="1.5"/>'
      + '<rect x="30" y="27" width="9" height="70" rx="4" fill="' + hex + '"/>'
      + '<rect x="81" y="27" width="9" height="70" rx="4" fill="' + hex + '"/>'
      + '<path d="M20 74 h80 l-3 22 a8 8 0 0 1 -8 7 h-58 a8 8 0 0 1 -8 -7 z" fill="' + hex + '"'
      + (isWhite ? ' stroke="#d8d8d8" stroke-width="1.5"' : '') + '/>';
  }

  function toteSvg(hex) {
    return '<svg viewBox="0 0 120 104" aria-hidden="true" focusable="false">' + toteBody(hex) + '</svg>';
  }

  /* One charm on its cord. Drawn far-to-near by the caller, so a nearer charm
     covers the cord of the one behind it and the pair reads as depth rather
     than as two flat stickers. The symbols measure 30 wide from x=15, y=1. */
  function charm(kind, cx, top, tilt, s, anchorX, anchorY, lift) {
    var id = kind === 'pet' ? '#personalizer-ic-pet' : '#personalizer-ic-doll';
    // On the bag a shadow is what separates "hanging off the rim" from
    // "printed on it" — the drawing alone is flat and reads as the latter.
    var shade = lift ? ' style="filter:drop-shadow(0 2px 3px rgba(35,0,81,.26))"' : '';
    var spin = tilt ? ' rotate(' + tilt + ' ' + cx + ' ' + top + ')' : '';
    // A cord under a charm's weight is straight, not slack.
    return '<path d="M' + anchorX + ' ' + anchorY + ' L' + cx + ' ' + (top + 5)
      + '" stroke="' + CORD + '" stroke-width="1.7" stroke-linecap="round"/>'
      + '<circle cx="' + anchorX + '" cy="' + anchorY + '" r="2.3" fill="none" stroke="' + RING + '" stroke-width="1.6"/>'
      + '<g' + shade + ' transform="translate(' + (cx - 30 * s).toFixed(2) + ',' + (top - s).toFixed(2) + ') scale(' + s + ')' + spin + '">'
      + '<use href="' + id + '"></use></g>';
  }

  /* Head-only portrait for the bag front — the face is what gets embroidered,
     so the charm's body and hanging cord would both be lies here.
     Drawn around a local centre of (30,17), 30 wide. */
  function head(kind, cx, cy, s) {
    var parts = kind === 'pet'
      ? '<path d="M22 10 q-8 1 -7 12 q0 6 6 5 z" fill="#8b5e34"/>'
        + '<path d="M38 10 q8 1 7 12 q0 6 -6 5 z" fill="#8b5e34"/>'
        + '<circle cx="30" cy="17" r="10" fill="#d9a066"/>'
        + '<ellipse cx="30" cy="20.5" rx="5.5" ry="4.2" fill="#f3e2c7"/>'
        + '<circle cx="30" cy="18.8" r="1.7" fill="#3a2a1c"/>'
        + '<circle cx="26" cy="14.5" r="1.4" fill="#222"/><circle cx="34" cy="14.5" r="1.4" fill="#222"/>'
      : '<circle cx="30" cy="18" r="9" fill="#f2c9a8"/>'
        + '<path d="M21 16 a9 9 0 0 1 18 0 l-2.5 -5 h-13 z" fill="#6b4a36"/>'
        + '<circle cx="27" cy="17" r="1.2" fill="#222"/><circle cx="33" cy="17" r="1.2" fill="#222"/>'
        + '<path d="M28 21 q2 1.8 4 0" stroke="#222" stroke-width="1" fill="none" stroke-linecap="round"/>';
    return '<g transform="translate(' + (cx - 30 * s).toFixed(2) + ',' + (cy - 17 * s).toFixed(2) + ') scale(' + s + ')">'
      + parts + '</g>';
  }

  /* Counts past the drawn maximum keep the 3-piece scene and say the rest —
     six charms at this size would be mush. */
  function overflowBadge(n) {
    return '<text x="114" y="108" text-anchor="end" font-size="14" font-weight="800" fill="#230051">&#215;' + n + '</text>';
  }

  function svg(orderType, n, kind, hex) {
    kind = kind === 'pet' ? 'pet' : 'doll';
    var count = Math.max(1, parseInt(n, 10) || 1);
    var slot = Math.min(count, 3);
    var inner = '';

    if (orderType === 'charm') {
      SOLO[slot].forEach(function (c) {
        inner += charm(kind, c[0], c[1], c[2], 0.95, c[0], c[1] - 5, false);
      });
    } else if (orderType === 'tote') {
      inner = '<g transform="' + BAG_SHIFT + '">' + toteBody(hex) + '</g>';
      var f = FACES[slot];
      f.x.forEach(function (x) { inner += head(kind, x, 55, f.s); });
    } else {
      inner = '<g transform="' + BAG_SHIFT + '">' + toteBody(hex) + '</g>';
      CHARMS_ON_BAG[slot].forEach(function (c) {
        inner += charm(kind, c[0], c[1], c[2], 0.62, c[3], CLIP_Y, true);
      });
    }
    if (count > 3) inner += overflowBadge(count);

    return '<svg class="personalizer__bag-art" viewBox="' + VIEWBOX + '" aria-hidden="true" focusable="false">'
      + inner + '</svg>';
  }

  /* --- Memorial dolls: N crochet dolls, each beside its own photo card ----
     The count preview for the memorial personalizer. Deliberately calm —
     cream doll, sage sweater, a card with a photo corner and two quiet lines
     of "verse" — because this scene sits on a grief product; nothing here may
     read as playful. Class is its own (not personalizer__bag-art), which the
     quiet skin hides. */

  function memorialDoll(cx, baseY, s) {
    // A remembrance figure drawn the way the dolls are actually made: a full
    // amigurumi body — arms, legs, little shoes — with crochet rows suggested
    // by faint stitch arcs, angel wings behind the shoulders and a thin gold
    // halo. Local coords put y=0 at the feet so every size shares a baseline.
    var stitch = 'fill="none" stroke="#7d947f" stroke-width="0.9" opacity="0.55"';
    var headStitch = 'fill="none" stroke="#caa987" stroke-width="0.9" opacity="0.5"';
    return '<g transform="translate(' + cx + ',' + baseY + ') scale(' + s + ')">'
      // wings, behind everything
      + '<path d="M-11 -34 q-17 -9 -16 -27 q11 4 16 15 z" fill="#f3eee2" stroke="#e2dac6" stroke-width="1"/>'
      + '<path d="M11 -34 q17 -9 16 -27 q-11 4 -16 15 z" fill="#f3eee2" stroke="#e2dac6" stroke-width="1"/>'
      + '<path d="M-11 -32 q-12 -6 -11 -18 q8 3 11 11 z" fill="#faf7ef"/>'
      + '<path d="M11 -32 q12 -6 11 -18 q-8 3 -11 11 z" fill="#faf7ef"/>'
      // halo
      + '<ellipse cx="0" cy="-66" rx="9.5" ry="3" fill="none" stroke="#d9c28a" stroke-width="2"/>'
      // head: yarn rows as concentric arcs under the hair
      + '<circle cx="0" cy="-48" r="12" fill="#f2d5b8"/>'
      + '<path d="M-9 -44 a10 10 0 0 0 18 0" ' + headStitch + '/>'
      + '<path d="M-7 -40.5 a8 8 0 0 0 14 0" ' + headStitch + '/>'
      + '<path d="M-13 -52 a13 13 0 0 1 26 0 q0 -6 -6 -8 h-14 q-6 2 -6 8" fill="#8b6f57"/>'
      + '<path d="M-10 -56 a11 11 0 0 1 20 0" fill="none" stroke="#7a6049" stroke-width="0.9" opacity="0.6"/>'
      + '<circle cx="-4.5" cy="-49" r="1.2" fill="#4a4038"/><circle cx="4.5" cy="-49" r="1.2" fill="#4a4038"/>'
      + '<path d="M-2.2 -43.5 h4.4" stroke="#4a4038" stroke-width="1.1" fill="none" stroke-linecap="round"/>'
      // arms: sleeves down the sides, small yarn hands
      + '<rect x="-17.5" y="-35" width="6.5" height="18" rx="3.2" fill="#8aa58c"/>'
      + '<rect x="11" y="-35" width="6.5" height="18" rx="3.2" fill="#8aa58c"/>'
      + '<circle cx="-14.2" cy="-15.5" r="3" fill="#f2d5b8"/><circle cx="14.2" cy="-15.5" r="3" fill="#f2d5b8"/>'
      // body: sweater with crochet rows
      + '<rect x="-12" y="-37" width="24" height="24" rx="8" fill="#9eb5a0"/>'
      + '<path d="M-11 -30 h22 M-11.6 -25 h23.2 M-11 -20 h22" ' + stitch + '/>'
      + '<rect x="-12" y="-37" width="24" height="6" rx="3" fill="#8aa58c"/>'
      // legs and shoes
      + '<rect x="-9" y="-14" width="7" height="10" rx="3.4" fill="#e8d9c2"/>'
      + '<rect x="2" y="-14" width="7" height="10" rx="3.4" fill="#e8d9c2"/>'
      + '<ellipse cx="-5.5" cy="-2" rx="4.6" ry="2.6" fill="#8b6f57"/>'
      + '<ellipse cx="5.5" cy="-2" rx="4.6" ry="2.6" fill="#8b6f57"/>'
      + '</g>';
  }

  function memorialCard(cx, baseY, s, tilt) {
    // The 6x4 photo card: white stock, a photo with a face hint, two faint
    // lines standing in for the verse, a small heart.
    return '<g transform="translate(' + cx + ',' + baseY + ') scale(' + s + ') rotate(' + (tilt || 0) + ')">'
      + '<rect x="-24" y="-34" width="48" height="34" rx="3" fill="#fffdf8" stroke="#ddd5c4" stroke-width="1.2"/>'
      + '<rect x="-19" y="-29" width="17" height="17" rx="2" fill="#e8dfcf"/>'
      + '<circle cx="-10.5" cy="-21.5" r="4.6" fill="#d9c4a8"/>'
      + '<path d="M3 -26 h16 M3 -20.5 h16 M3 -15 h11" stroke="#cfc6b2" stroke-width="1.6" stroke-linecap="round"/>'
      + '<path d="M-12 -7 c-1.6 -2.4 -4.8 -1 -4.4 1.4 c.3 1.8 2.6 3 4.4 4.2 c1.8 -1.2 4.1 -2.4 4.4 -4.2 c.4 -2.4 -2.8 -3.8 -4.4 -1.4z" fill="#d9a8a8"/>'
      + '</g>';
  }

  /* One doll-and-card pair per unit ordered, shoulder to shoulder. Every doll
     stands on the same baseline and the viewBox is cropped to the tallest
     figure — the box holds drawing, not air. */
  var DOLL_LAYOUT = {
    1: { s: 1, pairs: [[105, 170]] },
    2: { s: 0.85, pairs: [[46, 104], [176, 234]] },
    3: { s: 0.62, pairs: [[28, 64], [118, 154], [208, 244]] },
  };

  function dollScene(n) {
    var count = Math.max(1, Math.min(3, parseInt(n, 10) || 1));
    var L = DOLL_LAYOUT[count];
    var inner = '';
    L.pairs.forEach(function (p, i) {
      inner += memorialDoll(p[0], 108, L.s);
      inner += memorialCard(p[1], 106, L.s, i % 2 ? 2.5 : -2.5);
    });
    return '<svg class="personalizer__doll-art" viewBox="0 34 276 78" aria-hidden="true" focusable="false">'
      + inner + '</svg>';
  }

  /* Count-card miniature: n hanging charms in a row — ring, clasp stub and a
     floppy-eared pup each. Replaces the paw emoji on pages whose count IS a
     charm count (config.countIconArt "charm"), so every card previews its own
     combination instead of tallying pawprints. */
  function charmRow(n, kind) {
    var units = '';
    for (var i = 0; i < n; i++) {
      var body;
      if (kind === 'people') {
        // person charm — alternate her/him so a pair reads as a couple, the
        // third goes back to her (a friend, a little one)
        var her = i % 2 === 0;
        body = '<circle cx="0" cy="19" r="7.6" fill="#f2c9a8"/>'
          + (her
            ? '<path d="M-7.6 18 a7.6 7.6 0 0 1 15.2 0 l-1.6 -5 h-12 z" fill="#5a3a26"/>'
              + '<path d="M-7.4 18 q-1.6 8 0.4 13 M7.4 18 q1.6 8 -0.4 13" fill="none" stroke="#5a3a26" stroke-width="2.6" stroke-linecap="round"/>'
            : '<path d="M-7.6 18 a7.6 7.6 0 0 1 15.2 0 l-2 -3.5 h-11.2 z" fill="#e8dcc4"/>'
              + '<path d="M-8.4 16.6 h16.8" stroke="#d9cbb0" stroke-width="2.2" stroke-linecap="round"/>')
          + '<circle cx="-2.6" cy="19.5" r="1" fill="#222"/><circle cx="2.6" cy="19.5" r="1" fill="#222"/>'
          + '<path d="M-1.6 22.6 q1.6 1.4 3.2 0" stroke="#b4675c" stroke-width="1" fill="none" stroke-linecap="round"/>'
          + '<rect x="-6" y="26" width="12" height="11.6" rx="4.6" fill="' + (her ? '#2f4b8f' : '#2b63c9') + '"/>';
      } else {
        body = '<path d="M-7.4 12.5 q-4.6 .5 -4.2 6.4 q0 2.8 3.3 2.4 z" fill="#8b5e34"/>'
          + '<path d="M7.4 12.5 q4.6 .5 4.2 6.4 q0 2.8 -3.3 2.4 z" fill="#8b5e34"/>'
          + '<circle cx="0" cy="19" r="7.9" fill="#d9a066"/>'
          + '<ellipse cx="0" cy="22" rx="3.9" ry="2.9" fill="#f3e2c7"/>'
          + '<circle cx="0" cy="20.6" r="1.1" fill="#3a2a1c"/>'
          + '<circle cx="-2.9" cy="17.3" r="1" fill="#222"/><circle cx="2.9" cy="17.3" r="1" fill="#222"/>'
          + '<rect x="-5.9" y="26" width="11.8" height="11.4" rx="5" fill="#d9a066"/>'
          + '<ellipse cx="0" cy="31" rx="3.9" ry="4.8" fill="#f3e2c7"/>';
      }
      units += '<g transform="translate(' + (16 + i * 32) + ',5)">'
        + '<circle cx="0" cy="4" r="4.4" fill="none" stroke="#b8bcc4" stroke-width="2.4"/>'
        + '<path d="M0 8.6 v2.8" stroke="#8b7355" stroke-width="1.6"/>'
        + body + '</g>';
    }
    return '<svg viewBox="0 0 ' + (n * 32) + ' 44" aria-hidden="true" focusable="false">' + units + '</svg>';
  }

  window.PersonalizerBagArt = { svg: svg, toteSvg: toteSvg, dollScene: dollScene, charmRow: charmRow };
})();
